import { memo } from 'react';
import styles from '../../styles/WeaponPlate.module.css';
import clsx from '../../utils/clsx';
import WeaponIcon from '../WeaponIcon';
import AmmoMeter from './AmmoMeter';
import WeaponSpecialChips from './WeaponSpecialChips';
import { DAMAGE_TYPE_COLORS } from '../../engine/constants';
import { ammoBand } from '../../engine/ammoRamp';
import { rackPlateView, type RackPlateView } from '../../engine/weaponEditor/plate';
import type { Slot } from '../../types';

interface WeaponPlateProps {
  slot: Slot;
  /** Rack position, 0-based. Drives the ordinal and every callback. */
  index: number;
  total: number;
  /** The weapon in your hands - gold spine, glow, full detail. */
  armed: boolean;
  locked: boolean;
  dragging?: boolean;
  /** Show the insertion marker above this plate. */
  dropBefore?: boolean;
  /**
   * How far this plate is from the one in focus, and which side of it.
   *
   * The rack recedes away from whatever you are looking at: smaller,
   * fainter, tilted away. Computed in JS and handed to CSS as two custom
   * properties, so the look is all in the stylesheet and the arithmetic
   * is somewhere it can be read.
   */
  distance?: number;
  direction?: -1 | 0 | 1;
  /** True for the weapon this combatant would actually parry with. */
  isParryWeapon?: boolean;
  onSelect: (index: number) => void;
  onEdit: (index: number) => void;
  /** Double-click: arm and jump to the Controls cockpit (v8.226.0). */
  onConfigure?: ((index: number) => void) | null;
  onContextMenu?: (index: number, clientX: number, clientY: number) => void;
  onMoveBy?: (index: number, delta: number) => void;
  /** Owns the roving tabIndex - only the cursor plate is tabbable. */
  focused?: boolean;
  plateRef?: (el: HTMLButtonElement | null) => void;
  onKeyDown?: (e: React.KeyboardEvent, index: number) => void;
  onDragStart?: (index: number) => void;
  onDragOver?: (index: number) => void;
  onDrop?: (index: number) => void;
  onDragEnd?: () => void;
}

/**
 * One weapon on the rack.
 *
 * A plate is a button first - clicking arms the weapon - carrying the
 * whole profile at a readable size, in the bench data plate's grammar
 * (plan armoury, part 7): a caption over each figure. No figure is below
 * 12px and no caption below 10px; the grid it replaces ran to 7px and
 * truncated every name to an ellipsis.
 *
 * HOW MUCH IT SHOWS IS TWO INDEPENDENT QUESTIONS. Panel width sets the
 * floor for every plate, through `@container` queries in the stylesheet -
 * this component always renders the full markup and lets CSS decide. Being
 * ARMED adds the magazine gauge, the modules by name, the conditional
 * rules and the complete special list on top. So a
 * wide panel is dense, a narrow one degrades to a legible list rather than
 * to truncated mush, and the weapon being fired is always the most
 * detailed thing on screen.
 *
 * `aria-label` is the weapon NAME and must stay that way - `selectWeapon`
 * in e2e/helpers.js matches it as a substring.
 *
 * NO OVERRIDE MARKER, deliberately. The plan called for SlotEditor's `*`
 * hint on a weapon carrying per-weapon overrides, but a projected Slot
 * holds the RESOLVED value with no flag saying where it came from - the
 * four optional fields live on CharacterWeapon and are deliberately stored
 * sparsely (ADR 2026-08-22). Surfacing it means the projection carrying
 * the fact, which is a data-model change and does not belong in a
 * rendering commit. Deferred to wip.md.
 */
/** Name, class word, the ARMED stencil, the PARRY tag, the Fury mark, and the ordinal. */
function PlateHead({
  slot,
  index,
  armed,
  isParryWeapon,
  kind,
}: Pick<WeaponPlateProps, 'slot' | 'index' | 'armed' | 'isParryWeapon'> & { kind: string }) {
  return (
    <span className={styles.head}>
      <span className={styles.name}>{slot.weaponName || `Weapon ${index + 1}`}</span>
      {/*
        THE CLASS WORD, not a glyph (plan armoury, part 7): the drawn barrel
        read as a megaphone and the blade as a bayonet, and the emoji that
        followed could not say "pistol". The word, and its tags, can.
      */}
      <span className={styles.kind}>{kind}</span>
      {/* Stencilled, aria-hidden: aria-selected already says this. */}
      {armed && (
        <span className={styles.armedTag} aria-hidden="true">
          ARMED
        </span>
      )}
      {isParryWeapon && (
        <span className={styles.parryMark} title="Parries with this weapon">
          PARRY
        </span>
      )}
      {/* An ICON, not a word like PARRY: Fury is on nearly every Marine's every weapon, and a word there is noise. */}
      {slot.righteousFury && (
        <span className={styles.fury} title="Righteous Fury">
          <WeaponIcon name="fury" size={10} />
        </span>
      )}
      {/* Dimmed past 9: the keyboard shortcut stops there. */}
      <span className={clsx(styles.ordinal, index > 8 && styles.ordinalMuted)}>{index + 1}</span>
    </span>
  );
}

/** One arc per Signature step past silent: a dot alone is silent, three arcs deafening. */
const arc = (r: number) => `M${5 + r * 0.55},${10 - r * 0.7} Q${5 + r * 1.1},10 ${5 + r * 0.55},${10 + r * 0.7}`;

function SignatureMark({ signature }: { signature: RackPlateView['signature'] }) {
  return (
    <span className={styles.signature} title="Signature - how far it is heard" data-testid="plate-signature">
      <svg width="30" height="18" viewBox="0 0 34 20" aria-hidden="true" className={styles.sigMark}>
        <circle cx="5" cy="10" r="2.2" />
        <path d={[6, 11, 16].slice(0, signature.arcs).map(arc).join(' ')} />
      </svg>
      <span className={styles.sigWord}>{signature.word}</span>
    </span>
  );
}

/** The data plate's cells on the rack: a caption over each figure, damage leading; then the signature. */
function PlateCells({ view }: { view: RackPlateView }) {
  return (
    <span className={styles.cells}>
      {view.cells.map((c) => (
        <span key={c.label} className={clsx(styles.cell, c.folds && styles.fold)}>
          <span className={styles.caption}>{c.label}</span>
          <span className={clsx(styles.figure, c.lead && styles.lead)}>{c.value}</span>
        </span>
      ))}
      <span className={styles.spacer} />
      <SignatureMark signature={view.signature} />
    </span>
  );
}

/** What only the weapon in your hands says: its modules by name, and each conditional rule. */
function ArmedLines({ view }: { view: RackPlateView }) {
  return (
    <>
      {view.modules.length > 0 && (
        <span className={styles.line} data-testid="plate-modules">
          <span className={styles.caption}>MODULES</span>
          {view.modules.map((m) => (
            <span key={m} className={styles.module}>
              {m}
            </span>
          ))}
        </span>
      )}
      {view.when.map((line) => (
        <span key={line} className={styles.line} data-testid="plate-when">
          <span className={styles.caption}>WHEN</span>
          <span>{line}</span>
        </span>
      ))}
    </>
  );
}

/** A click that does not reach the rack behind (which closes the menu). */
const own = (action: () => void) => (e: React.MouseEvent) => {
  e.stopPropagation();
  action();
};

/**
 * Reorder without a mouse. `visibility: hidden` on the unavailable
 * direction so nothing shifts - the TargetGearTab precedent.
 */
function PlateRail({
  slot,
  index,
  total,
  onMoveBy,
}: Pick<WeaponPlateProps, 'slot' | 'index' | 'total'> & { onMoveBy: NonNullable<WeaponPlateProps['onMoveBy']> }) {
  return (
    <span className={styles.rail}>
      <button
        type="button"
        className={styles.railBtn}
        style={{ visibility: index === 0 ? 'hidden' : undefined }}
        aria-label={`Move ${slot.weaponName} up`}
        onClick={own(() => onMoveBy(index, -1))}
      >
        ▲
      </button>
      <button
        type="button"
        className={styles.railBtn}
        style={{ visibility: index === total - 1 ? 'hidden' : undefined }}
        aria-label={`Move ${slot.weaponName} down`}
        onClick={own(() => onMoveBy(index, 1))}
      >
        ▼
      </button>
    </span>
  );
}

/**
 * The plate's own editor door (v8.226.0): double-click jumps to the
 * cockpit now, so the pencil is how a plate opens its slot editor
 * without the context menu.
 */
function PlatePencil({ slot, onEdit }: { slot: Slot; onEdit: () => void }) {
  return (
    <button
      type="button"
      className={styles.pencil}
      aria-label={`Edit ${slot.weaponName}`}
      title="Edit this weapon slot"
      onClick={own(onEdit)}
    >
      ✎
    </button>
  );
}

/** The plate's drag, click, double-click and context-menu handlers, all speaking its rack position. */
function plateHandlers(p: WeaponPlateProps) {
  const { index, onSelect, onEdit, onConfigure, onContextMenu, onKeyDown, onDragStart, onDragOver, onDrop, onDragEnd } =
    p;
  return {
    onKeyDown: (e: React.KeyboardEvent) => onKeyDown?.(e, index),
    onDragStart: () => onDragStart?.(index),
    onDragEnd: () => onDragEnd?.(),
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      onDragOver?.(index);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      onDrop?.(index);
    },
    onClick: own(() => onSelect(index)),
    onDoubleClick: () => (onConfigure ? onConfigure(index) : onEdit(index)),
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onContextMenu?.(index, e.clientX, e.clientY);
    },
  };
}

function WeaponPlate(props: WeaponPlateProps) {
  const { slot, index, total, armed, locked, dragging, dropBefore, distance = 0, direction = 0 } = props;
  const { isParryWeapon, onEdit, onMoveBy, focused, plateRef } = props;
  const isMelee = slot.weaponType === 'melee';
  const dmg = DAMAGE_TYPE_COLORS[slot.damageType] || DAMAGE_TYPE_COLORS.I;
  const view = rackPlateView(slot, armed);
  /**
   * A DRY WEAPON RUNS RED - frame, spine and glow. This is the app's red
   * grammar (red = something is wrong) doing tactical duty: a glance at
   * the drum shows which chambers are empty, and it outranks the armed
   * gold because a gun you are holding with nothing in it is exactly the
   * alarm that matters. Melee weapons have no magazine and are never dry
   * (`ammoBand` guards that).
   */
  const dry = !isMelee && ammoBand(slot.ammo?.current ?? 0, slot.ammo?.max ?? 0) === 'empty';

  return (
    <div
      className={clsx(styles.row, dropBefore && styles.dropBefore)}
      style={{ '--dist': distance, '--dir': direction } as React.CSSProperties}
    >
      <button
        type="button"
        role="option"
        aria-selected={armed}
        aria-label={slot.weaponName || `Weapon ${index + 1}`}
        // Roving tabIndex: one stop for the whole rack, then arrows within
        // it. Forty weapons must not be forty tab stops.
        tabIndex={focused ? 0 : -1}
        ref={plateRef}
        data-testid={`weapon-plate-${index}`}
        className={clsx(styles.plate, armed && styles.armed, dry && styles.dry, dragging && styles.dragging)}
        // The spine is the damage type, in the one colour the app already
        // has for it - structural, rather than a 7px dot.
        style={{ '--spine': dmg.bg } as React.CSSProperties}
        draggable={!locked}
        {...plateHandlers(props)}
      >
        <PlateHead slot={slot} index={index} armed={armed} isParryWeapon={isParryWeapon} kind={view.kind} />
        <PlateCells view={view} />
        {armed && <AmmoMeter ammo={slot.ammo} showReload />}
        {armed && <ArmedLines view={view} />}
        <WeaponSpecialChips specialRules={slot.specialRules} limit={armed ? undefined : 3} />
      </button>

      {!locked && <PlatePencil slot={slot} onEdit={() => onEdit(index)} />}
      {onMoveBy && !locked && <PlateRail slot={slot} index={index} total={total} onMoveBy={onMoveBy} />}
    </div>
  );
}

export default memo(WeaponPlate);
